import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as notificationActionCreators from '../../../redux/notifications'
import * as licenseActions from '../../../redux/css-admin/licenseManager/actions'
import * as licenseOperations from '../../../redux/css-admin/licenseManager/operations'
import moment from 'moment'
import {
  formatSiteForSchoolLookUp,
  formatNetsuiteCustomerForSchoolLookUp,
  searchNSCustomersDebounced,
} from '../../../lib/editNetSuite'
import { licenseShape } from '../../../lib/shapes'
import { Formik } from 'formik'
import {
  Alert,
  Badge,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap'
import { licenseSchema } from '../../../lib/schemas'
import { ProductType, Category, extractExpirationInfo } from '../../../lib/util'
import ErrorBoundary from 'components/ErrorBoundary'
import FormikInputField from 'components/FormikInputField'
import FormikDateField from 'components/FormikDateField'
import EditLicensePrevSub from 'components/EditLicensePrevSub'
import Tooltip from 'components/Tooltip'
import FieldsetItem from '../../../components/FieldsetItem'
import FieldsetItemLarge from '../../../components/FieldsetItemLarge'
import Notification from 'components/Notification'
import Select from 'components/Select'
import SchoolLookUp from 'components/SchoolLookUp'
import SiteChangeConfirmation from '../../../components/EditLicenseModalSiteChangeConfirmation/component'
import { getSites, getSitesUsers } from '../../../lib/apiHooks/SitesApi'
import { getLicense } from '../../../lib/apiHooks/LicensesApi'
import { getSubscription } from '../../../lib/apiHooks/SubscriptionApi'

const Tabs = {
  Product: 1,
  Assignee: 2,
  Move: 3,
  Cancel: 4,
}

const EditLicenseModal = props => {
  const {
    hasInactiveNetSuiteCustomer, 
    isOpen,
    license,
    licenseId,
    refData,
    saveLicenseAndHandle,
    siteId,
    toggle,
    updateSubscription,
  } = props

  const [activeTab, setActiveTab] = useState(Tabs.Product)
  const [currentSchool, setCurrentSchool] = useState({
    city: '',
    companyName: '',
    country: '',
    netsuiteCustomerId: '',
    nsCustomerId: '',
    organizationName: '',
    region: '',
  })
  const [previousCustomerId, setPreviousCustomerId] = useState('')
  const [
    previousCustomerIdValidation,
    setPreviousCustomerIdValidation,
  ] = useState('')
  const [previousSubscription, setPreviousSubscription] = useState(null)
  const [selectedSku, setSelectedSku] = useState({})
  const [skuWarning, setSkuWarning] = useState([])
  const [subscription, setSubscription] = useState({})
  const [skuListOrdered, setSkuListOrdered] = useState([])
  const [selectedLicense, setSelectedLicense] = useState()
  const [selectedSites, setSelectedSites] = useState()
  const [selectedSitesUsers, setSelectedSitesUsers] = useState()

  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = getSubscription({
    subscriptionId: selectedLicense && selectedLicense.subscriptionId,
  })

  const {
    data: prevSubData,
    loading: prevSubLoading,
    error: prevSubError,
  } = getSubscription({
    subscriptionId: selectedLicense && selectedLicense.previousSubscriptionId,
  })

  const {
    data: licenseData,
    loading: licenseLoading,
    error: licenseError,
  } = getLicense(licenseId)

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = getSites({
    licenseId: licenseId || (license && license.Id),
    siteId: siteId || (license && license.siteId),
  })

  const {
    data: sitesUsersData,
    loading: sitesUsersLoading,
    error: sitesUsersError,
  } = getSitesUsers(
    selectedSites && selectedSites.length > 0 && selectedSites[0].id,
  )

  useEffect(() => {
    if (props.subscription) {
      setSubscription(props.subscription)
    } else {
      if (subscriptionData && !subscriptionLoading && !subscriptionError) {
        setSubscription(subscriptionData[0])
      }
    }
  }, [subscriptionData, subscriptionLoading, subscriptionError])

  useEffect(() => {
    if (license && selectedLicense == null) {
      setSelectedLicense(license)
    } else if (!licenseLoading && !licenseError) {
      setSelectedLicense(licenseData)
    }
  }, [license, licenseData, licenseLoading, licenseError])

  useEffect(() => {
    if (!sitesLoading && !sitesError) {
      setSelectedSites(sitesData)
    }
  }, [sitesData, sitesLoading, sitesError])

  useEffect(() => {
    if (!sitesUsersLoading && !sitesUsersError) {
      setSelectedSitesUsers(sitesUsersData)
    }
  }, [sitesUsersData, sitesUsersLoading, sitesUsersError])

  useEffect(() => {
    if (!hasInactiveNetSuiteCustomer 
          && selectedSites 
          && selectedSites.length > 0 
          && selectedSites[0].netSuiteCustomer 
          && !selectedSites[0].netSuiteCustomer.inactive
        ) {
      const site = formatSiteForSchoolLookUp(selectedSites[0])
      setCurrentSchool({ ...site })
    }
  }, [selectedSites])

  useEffect(() => {
    if (!selectedLicense) return
    if (selectedLicense.product && selectedLicense.product.sku) {
      setSelectedSku(selectedLicense.product.sku)
    }
  }, [selectedLicense])

  useEffect(() => {
    if (
      selectedLicense &&
      selectedLicense.previousSubscriptionId &&
      !prevSubLoading &&
      !prevSubError
    ) {
      setPreviousSubscription(prevSubData)
    } else {
      setPreviousSubscription(null)
    }
  }, [selectedLicense, prevSubData, prevSubLoading, prevSubError])

  const setCurrentTab = tab => setActiveTab(tab)

  const setValue = async (formik, fieldName, value, validate = true) =>
    await formik.setFieldValue(fieldName, value, validate)

  const schoolSelected = async (formik, site) => {
    const {
      city,
      companyName,
      country,
      netsuiteCustomerId,
      nsCustomerId,
      organizationName,
      region,
    } = site

    await setValue(formik, 'site.city', city)
    await setValue(formik, 'site.companyName', companyName)
    await setValue(formik, 'site.country', country)
    await setValue(formik, 'site.netsuiteCustomerId', netsuiteCustomerId)
    await setValue(formik, 'site.nsCustomerId', nsCustomerId)
    await setValue(formik, 'site.region', region)
    await setValue(formik, 'site.organizationName', organizationName)

    setPreviousCustomerId(nsCustomerId)
    setPreviousCustomerIdValidation('')

    if (formik.initialValues.nsCustomerId !== formik.values.nsCustomerId) {
      formik.setFieldValue('siteWasChanged', true)
      formik.setFieldValue('siteChangeChecked', false)
    }
  }

  const customerIdValidateAsync = (value, formik) => {
    let errorMessage = previousCustomerIdValidation

    try {
      if (value === previousCustomerId) return errorMessage
      if (!value) return

      const formattedValue = value.toString()
      if (!formattedValue.match(/^[0-9]*$/)) {
        errorMessage = 'Customer Id must contain only numbers'

        setPreviousCustomerId(value)
        setPreviousCustomerIdValidation(errorMessage)

        return errorMessage
      }

      // API VALIDATION (promise)
      return searchNSCustomersDebounced(value)
        .then(response => {
          let errorMessage = ''
          const netsuiteCustomer = response.data[0]
          const isValid = !!netsuiteCustomer
          if (isValid) {
            errorMessage = ''
            const currentSchool = formatNetsuiteCustomerForSchoolLookUp(
              netsuiteCustomer,
            )
            schoolSelected(formik, currentSchool)
            setCurrentSchool(currentSchool)
          } else errorMessage = `Cannot find Customer Id ${value}`

          setPreviousCustomerId(value)
          setPreviousCustomerIdValidation(errorMessage)

          return errorMessage
        })
        .catch(err => {
          return err.message
        })
    } catch (err) {
      setPreviousCustomerId(value)
      setPreviousCustomerIdValidation(err.message)
      return
    }
  }

  const handleChange = (formik, e) => {
    const name = e.target.name
    const value = e.target.value
    formik.setFieldValue(name, value, true)
  }

  const skuValidate = skuValue => {
    const errorMessage = ''

    if (!subscription) {
      console.warn('no subscription; cannot validate sku further')
      return errorMessage
    }

    const skuWarning = []
    // check to see if subscription has this sku
    const matchingSku = subscription.skus.filter(s => s.sku === skuValue)
    if (matchingSku.length === 0)
      skuWarning.push(
        "This licenses' subscription does not contain this product",
      )

    // check to see if there are _available_ skus on subscription
    const provisionedCount = subscription.licenses.filter(
      l => l.product.sku.skuValue === skuValue,
    )
    if (matchingSku.length > 0 && provisionedCount >= matchingSku.qty)
      skuWarning.push(
        `All ${matchingSku.qty} licenses for this sku have already been provisioned`,
      )

    // ensure all skus have the same duration
    const durationCount = [
      ...new Set(subscription.skus.map(s => lookupSku(s.sku).durationYears)),
    ]
    // Warning message here just notes that there are multiple products with different duration years
    if (durationCount.length > 1)
      skuWarning.push(
        'WARNING: This subscription contains SKUs with multiple durations, ensure the SKU selected is the correct duration before proceeding',
      )

    const thisLicenseSkuDuration = lookupSku(skuValue).durationYears
    const prevLicenseSkuDuration = lookupSku(
      selectedLicense.product.sku.skuValue,
    ).durationYears
    if (
      thisLicenseSkuDuration &&
      thisLicenseSkuDuration !== prevLicenseSkuDuration
    )
      skuWarning.push(
        `WARNING: The selected product duration (${
          thisLicenseSkuDuration > 365
            ? thisLicenseSkuDuration / 365 + ' years'
            : thisLicenseSkuDuration + ' days'
        }) does not match the current product duration (${
          prevLicenseSkuDuration > 365
            ? prevLicenseSkuDuration / 365 + ' years'
            : prevLicenseSkuDuration + ' days'
        })`,
      )

    const selectedSku = lookupSku(skuValue)
    setSelectedSku(selectedSku)
    setSkuWarning(skuWarning)
    return errorMessage
  }

  const lookupSku = sku => props.refData.skus.get(sku) || {}

  const handleSubmit = async (values, { setSubmitting }) => {
    values.sku = values.sku.toString()
    const site = values.site
    const reqObj = {
      licenseId: values.selectedLicense.id,
      site: {
        nsCustomerId: site.nsCustomerId || null,
        netsuiteCustomerId: site.netsuiteCustomerId,
        country: site.country,
        region: site.region,
        city: site.city,
        name: site.companyName || site.organizationName,
      },
      cancelledDate: values.cancelledDate,
      moveSubscription: values.moveSubscription,
    }
    if (values.sku) {
      reqObj.skuValue = values.sku.toString()
    }
    if (values.cancelledDate) {
      //For the cancel functionality theres no reason to look up the site so passing siteId
      reqObj.site.siteId = values.selectedLicense.siteId
    }

    await saveLicenseAndHandle(reqObj)
    if (updateSubscription) updateSubscription()
    toggle()
    setSubmitting(false)
  }

  const clearCancellationDate = formik =>
    formik.setFieldValue('cancelledDate', '', true)

  const schoolLookupIsValid = formikProps => {
    const site = formikProps.values.site
    if (!site) return false

    return (
      !!site.nsCustomerId ||
      !!site.netsuiteCustomerId ||
      !!site.organizationName
    )
  }

  const { customer } = subscription || {}
  const { companyName } = customer || ''
  const product = selectedLicense && selectedLicense.product

  const expiration = !selectedLicense
    ? {}
    : extractExpirationInfo(selectedLicense, 'YYYY-MM-DD')

  if (
    !_.isEmpty(selectedSku) &&
    !_.isEmpty(subscription) &&
    !_.isEmpty(selectedLicense)
  ) {
    const { licensesToAssign } = subscription
    const uniqueLicenses = _.uniqBy(licensesToAssign, 'sku')
    const filteredUniqueLicenses = _.filter(
      uniqueLicenses,
      uniqueLicense => uniqueLicense.sku !== product.sku,
    )

    if (_.isEmpty(skuListOrdered) && !_.isEmpty(filteredUniqueLicenses)) {
      const newSkuListOrdered = filteredUniqueLicenses.map(license =>
        refData.skus.get(license.sku),
      )
      if (!_.isEmpty(newSkuListOrdered)) {
        setSkuListOrdered(newSkuListOrdered)
      }
    }
  }

  return (
    <Modal
      backdrop="static"
      centered
      fade
      isOpen={isOpen}
      size="lg"
      toggle={toggle}
    >
      {selectedLicense && product && (
        <React.Fragment>
          <ModalHeader toggle={toggle}>
            License Edit&nbsp;<Badge>SS: {selectedLicense.id}</Badge>
            <Badge>NS: {selectedLicense.nsLicenseId}</Badge>
          </ModalHeader>
          <ErrorBoundary>
            <Formik
              initialValues={{
                selectedLicense: selectedLicense,
                previousSubscriptionId: selectedLicense.previousSubscriptionId,
                nsSubscriptionId: selectedLicense.nsSubscriptionId,
                originationDate: selectedLicense.originationDate,
                nsCustomerId: currentSchool.nsCustomerId || '',
                netsuiteCustomerId: currentSchool.netsuiteCustomerId,
                sku: parseInt(product.sku, 10) || product.sku.skuValue,
                site: currentSchool,
                cancelledDate: selectedLicense.cancelledDate
                  ? moment(selectedLicense.cancelledDate).format('YYYY-MM-DD')
                  : '',
                isCanceled: !!selectedLicense.cancelledDate,
                unlimitedClasses: selectedLicense.maxClassCount < 0,
                moveSubscription: false,
                siteChangeChecked: false,
                siteWasChanged: false,
              }}
              isInitialValid={true}
              enableReinitialize={true}
              onSubmit={handleSubmit}
              validationSchema={licenseSchema}
            >
              {props => {
                return (
                  <form onSubmit={props.handleSubmit}>
                    <ModalBody>
                      <fieldset>
                        <legend>Details</legend>
                        <Row>
                          <Col sm={8}>
                            <FieldsetItemLarge
                              data={companyName}
                              title="Customer Name"
                            />
                            <FieldsetItemLarge
                              data={selectedLicense.siteName}
                              title="Assigned To"
                            />
                            <FieldsetItemLarge
                              data={product.description || ''}
                              title="Product"
                            />
                            {product.type === Category.INDIVIDUAL.category &&
                              selectedSites &&
                              selectedSitesUsers &&
                              selectedSitesUsers.length === 0 && (
                                <FieldsetItem
                                  data="UNASSIGNED"
                                  title="Assigned Teacher"
                                />
                              )}

                            {product.type === Category.INDIVIDUAL.category &&
                              selectedSites &&
                              selectedSitesUsers &&
                              selectedSitesUsers.length > 0 && (
                                <FieldsetItem title="Assigned Teacher">
                                  <div>{selectedSitesUsers[0].email}</div>
                                  <h5 className="font-weight-bold">
                                    {`${selectedSitesUsers[0].firstName} 
                                    ${selectedSitesUsers[0].lastName}`}
                                  </h5>
                                </FieldsetItem>
                              )}
                          </Col>
                          <Col sm={4}>
                            <FieldsetItem
                              color="none"
                              data={expiration.startDate.formatted}
                              title="Start Date"
                            />
                            <FieldsetItem
                              color={expiration.isExpired ? 'danger' : 'none'}
                              title="Expiration"
                            >
                              {expiration.icon}{' '}
                              {expiration.expirationDate.formatted}
                            </FieldsetItem>
                            {selectedLicense.cancelledDate && (
                              <FieldsetItem
                                color="warning"
                                data={expiration.cancelledDate.formatted}
                                title="Cancellation"
                              />
                            )}
                            <FieldsetItem
                              color="none"
                              data={moment(selectedLicense.modifiedDate).format(
                                'YYYY-MM-DD',
                              )}
                              title="Modified Date"
                            />
                          </Col>
                        </Row>
                      </fieldset>

                      <Nav justified tabs>
                        <NavItem>
                          <NavLink
                            active={activeTab === Tabs.Product}
                            onClick={() => setCurrentTab(Tabs.Product)}
                          >
                            Product
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            active={activeTab === Tabs.Assignee}
                            onClick={() => setCurrentTab(Tabs.Assignee)}
                          >
                            Assignee
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            active={activeTab === Tabs.Move}
                            onClick={() => setCurrentTab(Tabs.Move)}
                          >
                            Move
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            active={activeTab === Tabs.Cancel}
                            onClick={() => setCurrentTab(Tabs.Cancel)}
                          >
                            Cancel
                          </NavLink>
                        </NavItem>
                      </Nav>
                      <TabContent activeTab={activeTab}>
                        <br />
                        <TabPane tabId={Tabs.Product}>
                          <div className="same-height">
                            <ErrorBoundary>
                              <fieldset>
                                <legend>Change Sku</legend>
                                <Row>
                                  <Col>
                                    <Select
                                      data={skuListOrdered}
                                      label="Product"
                                      name="sku"
                                      onChange={e => {
                                        handleChange(props, e)
                                        skuValidate(e.target.value)
                                      }}
                                      renderOptions={products => {
                                        return products.map(mProduct => (
                                          <option
                                            key={mProduct.sku || '0'}
                                            value={mProduct.sku || '0'}
                                          >
                                            {mProduct.sku}
                                            &ndash; {mProduct.skuCode}
                                            &ndash; {mProduct.name}
                                          </option>
                                        ))
                                      }}
                                      value={props.values.sku || ''}
                                      {...props}
                                      disabled={_.isEmpty(skuListOrdered)}
                                    />
                                  </Col>
                                </Row>
                                {skuWarning.length > 0 && (
                                  <Notification color="warning">
                                    <ul className="list-unstyled">
                                      {skuWarning.map((warn, i) => (
                                        <li key={i}>{warn}</li>
                                      ))}
                                    </ul>
                                  </Notification>
                                )}
                              </fieldset>
                              {product.type === Category.BULK.category &&
                                product.programCodes.includes(
                                  ProductType.MSP.skuCode,
                                ) && (
                                  <fieldset>
                                    <legend>Limits</legend>
                                    <Row>
                                      <Col sm={6} xs={6}>
                                        <FormikInputField
                                          disabled={
                                            product.type ===
                                            Category.INDIVIDUAL.category
                                          }
                                          label="Teachers Allowed"
                                          name="maxSeatCount"
                                          {...props}
                                        />
                                      </Col>
                                      <Col sm={4} xs={6}>
                                        <DefaultValue
                                          currentValue={
                                            props.values.maxSeatCount
                                          }
                                          defaultValue={
                                            selectedSku.maxSeatCount
                                          }
                                          onClick={() =>
                                            setValue(
                                              props,
                                              'maxSeatCount',
                                              selectedSku.maxSeatCount,
                                            )
                                          }
                                        />
                                      </Col>
                                    </Row>
                                  </fieldset>
                                )}
                            </ErrorBoundary>
                          </div>
                        </TabPane>
                        <TabPane tabId={Tabs.Assignee}>
                          <div className="same-height">
                            <ErrorBoundary>
                              <Alert color="info">
                                Assign license to site by entering the
                                site&#39;s NetSuite Customer ID OR by selecting
                                the site using dropdown menu.
                                <br />
                                If you use the dropdown menu, leave NetSuite
                                Customer ID blank.
                              </Alert>
                              <FormikInputField
                                label="Assigned Site's NetSuite Customer ID"
                                name="nsCustomerId"
                                type="text"
                                validate={values =>
                                  Tabs.Assignee === activeTab &&
                                  customerIdValidateAsync(values, props)
                                }
                                {...props}
                              />
                              {props.values.siteWasChanged && (
                                <SiteChangeConfirmation
                                  siteChangeChecked={
                                    props.values.siteChangeChecked
                                  }
                                  setFieldValue={props.setFieldValue}
                                />
                              )}
                              <hr />
                              <SchoolLookUp
                                disabled={!!props.values.nsCustomerId}
                                key={currentSchool.netsuiteCustomerId}
                                onSelection={data =>
                                  schoolSelected(props, data)
                                }
                                siteData={currentSchool}
                              />
                            </ErrorBoundary>
                          </div>
                        </TabPane>
                        <TabPane tabId={Tabs.Move}>
                          <div className="same-height">
                            <EditLicensePrevSub
                              name="moveSubscription"
                              previousSubscription={previousSubscription}
                              setFieldValue={props.setFieldValue}
                              values={props.values.moveSubscription}
                            />
                          </div>
                        </TabPane>
                        <TabPane tabId={Tabs.Cancel}>
                          <FormikDateField
                            label="Cancellation Date"
                            name="cancelledDate"
                            placeholder="yyyy-mm-dd"
                            {...props}
                          />
                          <Button
                            className="btn-fixed-width"
                            onClick={() => clearCancellationDate(props)}
                          >
                            Clear Cancellation
                          </Button>
                        </TabPane>
                      </TabContent>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        className="btn-fixed-width"
                        color="link"
                        disabled={props.isSubmitting}
                        onClick={toggle}
                        style={{
                          fontSize: '18px',
                          fontFamily: 'Molde-Medium',
                          color: '#5F5F5F',
                          margin: '12px 0px auto',
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="btn-fixed-width"
                        color={props.isSubmitting ? 'disabled' : 'primary'}
                        disabled={
                          props.isSubmitting ||
                          props.isValidating ||
                          !props.isValid ||
                          !props.touched ||
                          !schoolLookupIsValid(props)
                        }
                        style={{
                          margin: '12px 4px 24px 48px',
                          width: '130px',
                          height: '42px',
                          fontSize: '18px',
                          fontFamily: 'Molde-Medium',
                        }}
                        type="submit"
                      >
                        Submit
                      </Button>
                    </ModalFooter>
                  </form>
                )
              }}
            </Formik>
          </ErrorBoundary>
        </React.Fragment>
      )}
    </Modal>
  )
}

const DefaultValue = ({ currentValue, defaultValue, onClick }) => {
  const isMatched = currentValue === defaultValue
  return (
    <FormGroup>
      <Label>Default Value</Label>
      <InputGroup>
        <InputGroupAddon addonType="prepend">
          {isMatched ? (
            <InputGroupText className="btn-success">
              <i className="fa fa-check" />
            </InputGroupText>
          ) : (
            <Tooltip title="apply">
              <Button color="danger" onClick={onClick}>
                <i className="fa fa-arrow-left" />
              </Button>
            </Tooltip>
          )}
        </InputGroupAddon>
        <Input readOnly value={defaultValue || ''} />
      </InputGroup>
    </FormGroup>
  )
}

EditLicenseModal.propTypes = {
  hasInactiveNetSuiteCustomer: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  license: licenseShape,
  licenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subscription: PropTypes.object,
  toggle: PropTypes.func.isRequired,
  updateSubscription: PropTypes.func,
}

const mapStateToProps = ({ refData, licenseManager }) => {
  return {
    refData: refData,
    ...licenseManager,
  }
}

export default connect(mapStateToProps, dispatch =>
  bindActionCreators(
    {
      ...notificationActionCreators,
      ...licenseActions,
      ...licenseOperations,
    },
    dispatch,
  ),
)(EditLicenseModal)



// WEBPACK FOOTER //
// ./src/containers/css-admin/licenses/EditLicenseModal.js